import { useState, useEffect, useCallback, useRef } from "react"
import { DataElementConfig } from "types/ReportingDashboard/elements.types"

/**
 * Configuration schema definition for element config validation
 */
export interface ConfigSchemaField {
  required: boolean
  type: "string" | "number" | "boolean" | "array" | "object"
  default?: unknown
  minLength?: number
  maxLength?: number
}

export interface ConfigSchema {
  [key: string]: ConfigSchemaField
}

/**
 * Validation result interface
 */
export interface ValidationResult {
  isValid: boolean
  errors: string[]
  missingFields?: string[]
}

/**
 * Custom validator function type
 */
export type ConfigValidator = (config: DataElementConfig) => ValidationResult

/**
 * Config transformer function type
 */
export type ConfigTransformer = (config: DataElementConfig) => DataElementConfig

/**
 * Options for updating configuration
 */
export interface UpdateConfigOptions {
  skipSave?: boolean
  immediate?: boolean
  skipNextUpdate?: boolean
}

/**
 * Options for saving configuration
 */
export interface SaveConfigOptions {
  force?: boolean
}

/**
 * Parameters for useElementConfig hook
 */
export interface UseElementConfigParams {
  elementId: string
  config: DataElementConfig | null | undefined
  onConfigUpdate: (config: DataElementConfig | null) => void
  defaultConfig?: DataElementConfig
  configSchema?: ConfigSchema
  configValidator?: ConfigValidator
  configTransformer?: ConfigTransformer
  autoSave?: boolean
  saveDelay?: number
}

/**
 * Return type for useElementConfig hook
 */
export interface UseElementConfigReturn {
  // Configuration state
  config: DataElementConfig
  isInitializing: boolean
  hasUnsavedChanges: boolean
  skipNextConfigUpdate: boolean

  // Configuration actions
  updateConfig: (
    updates: Partial<DataElementConfig>,
    options?: UpdateConfigOptions
  ) => void
  saveConfig: (
    configToSave?: DataElementConfig | null,
    options?: SaveConfigOptions
  ) => void
  resetConfig: () => void
  clearConfig: () => void
  skipNextUpdate: () => void

  // Validation
  validateConfig: (configToValidate: DataElementConfig) => ValidationResult

  // Utility functions
  getConfigValue: <T = unknown>(key: string, fallback?: T) => T
  setConfigValue: (
    key: string,
    value: unknown,
    options?: UpdateConfigOptions
  ) => void

  // Refs for advanced usage
  isGeneratingImageRef: React.MutableRefObject<boolean>
  setSkipNextConfigUpdate: React.Dispatch<React.SetStateAction<boolean>>
}

/**
 * Custom hook for managing element configuration lifecycle
 * Abstracts common patterns for loading, validating, and saving element configs
 *
 * @param params - Configuration options
 * @returns Configuration management utilities
 *
 * @example
 * const elementConfig = useElementConfig({
 *   elementId: 'chart-1',
 *   config: chartConfig,
 *   onConfigUpdate: handleConfigUpdate,
 *   defaultConfig: DEFAULT_CHART_CONFIG,
 *   configSchema: CHART_CONFIG_SCHEMA,
 *   configValidator: validateChartConfig,
 *   autoSave: true,
 *   saveDelay: 700,
 * })
 */
const useElementConfig = ({
  elementId,
  config,
  onConfigUpdate,
  defaultConfig = {} as DataElementConfig,
  configSchema = {},
  configValidator,
  configTransformer,
  autoSave = true,
  saveDelay = 700,
}: UseElementConfigParams): UseElementConfigReturn => {
  // Core state
  const [isInitializing, setIsInitializing] = useState<boolean>(true)
  const [skipNextConfigUpdate, setSkipNextConfigUpdate] =
    useState<boolean>(false)
  const [configState, setConfigState] =
    useState<DataElementConfig>(defaultConfig)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false)
  const [lastSavedConfig, setLastSavedConfig] =
    useState<DataElementConfig | null>(null)

  // Refs for preventing race conditions
  const isGeneratingImageRef = useRef<boolean>(false)
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  /**
   * Validates configuration against schema and custom validator
   */
  const validateConfig = useCallback(
    (configToValidate: DataElementConfig): ValidationResult => {
      // Schema validation
      const missingRequired = Object.entries(configSchema)
        .filter(([key, schema]) => {
          const configValue = (configToValidate as Record<string, unknown>)[key]
          return (
            schema.required &&
            (configValue === undefined || configValue === null)
          )
        })
        .map(([key]) => key)

      if (missingRequired.length > 0) {
        return {
          isValid: false,
          errors: [`Missing required fields: ${missingRequired.join(", ")}`],
          missingFields: missingRequired,
        }
      }

      // Custom validation
      if (configValidator) {
        const customValidation = configValidator(configToValidate)
        if (!customValidation.isValid) {
          return customValidation
        }
      }

      return { isValid: true, errors: [], missingFields: [] }
    },
    [configSchema, configValidator]
  )

  /**
   * Loads configuration with defaults and validation
   */
  const loadConfig = useCallback(
    (incomingConfig: DataElementConfig | null | undefined) => {
      if (isGeneratingImageRef.current) return

      setIsInitializing(true)

      // Merge with defaults
      const mergedConfig: DataElementConfig = {
        ...defaultConfig,
        ...incomingConfig,
      }

      // Apply any transformation
      const finalConfig = configTransformer
        ? configTransformer(mergedConfig)
        : mergedConfig

      // Validate
      const validation = validateConfig(finalConfig)
      if (!validation.isValid) {
        console.warn(
          `Invalid config for element ${elementId}:`,
          validation.errors
        )
      }

      setConfigState(finalConfig)
      setLastSavedConfig(finalConfig)
      setHasUnsavedChanges(false)

      // Brief delay to allow state to settle
      const timer = setTimeout(() => {
        setIsInitializing(false)
      }, 50)

      return () => clearTimeout(timer)
    },
    [defaultConfig, configTransformer, validateConfig, elementId]
  )

  /**
   * Updates configuration state
   */
  const updateConfig = useCallback(
    (
      updates: Partial<DataElementConfig>,
      options: UpdateConfigOptions = {}
    ) => {
      const { skipSave = false, immediate = false } = options

      setConfigState((prevConfig) => {
        const newConfig: DataElementConfig = { ...prevConfig, ...updates }

        if (!skipSave) {
          setHasUnsavedChanges(true)
          if (immediate) {
            setSkipNextConfigUpdate(false)
          }
        }

        return newConfig
      })

      if (options.skipNextUpdate) {
        setSkipNextConfigUpdate(true)
      }
    },
    []
  )

  /**
   * Saves configuration to parent component
   */
  const saveConfig = useCallback(
    (
      configToSave: DataElementConfig | null = null,
      options: SaveConfigOptions = {}
    ) => {
      const { force = false } = options

      if (!onConfigUpdate || (!force && skipNextConfigUpdate)) {
        if (skipNextConfigUpdate) {
          setSkipNextConfigUpdate(false)
        }
        return
      }

      const finalConfig = configToSave || configState

      // Validate before saving
      const validation = validateConfig(finalConfig)
      if (!validation.isValid) {
        console.error(
          `Cannot save invalid config for element ${elementId}:`,
          validation.errors
        )
        return
      }

      // Apply transformation if needed
      const transformedConfig = configTransformer
        ? configTransformer(finalConfig)
        : finalConfig

      // Add element ID to config
      const configWithId: DataElementConfig = {
        ...transformedConfig,
        id: elementId,
      }

      onConfigUpdate(configWithId)
      setLastSavedConfig(configWithId)
      setHasUnsavedChanges(false)
      setSkipNextConfigUpdate(false)
    },
    [
      onConfigUpdate,
      skipNextConfigUpdate,
      configState,
      validateConfig,
      configTransformer,
      elementId,
    ]
  )

  /**
   * Resets configuration to last saved state
   */
  const resetConfig = useCallback(() => {
    if (lastSavedConfig) {
      setConfigState(lastSavedConfig)
      setHasUnsavedChanges(false)
    }
  }, [lastSavedConfig])

  /**
   * Clears configuration
   */
  const clearConfig = useCallback(() => {
    setConfigState(defaultConfig)
    setHasUnsavedChanges(true)
    if (onConfigUpdate) {
      onConfigUpdate(null)
    }
  }, [defaultConfig, onConfigUpdate])

  /**
   * Utility to set skip flag for preventing update loops
   */
  const skipNextUpdate = useCallback(() => {
    setSkipNextConfigUpdate(true)
  }, [])

  /**
   * Gets a configuration value with optional fallback
   */
  const getConfigValue = useCallback(
    <T = unknown>(key: string, fallback?: T): T => {
      const value = (configState as Record<string, unknown>)[key]
      return (value ?? fallback) as T
    },
    [configState]
  )

  /**
   * Sets a single configuration value
   */
  const setConfigValue = useCallback(
    (key: string, value: unknown, options?: UpdateConfigOptions) => {
      updateConfig({ [key]: value } as Partial<DataElementConfig>, options)
    },
    [updateConfig]
  )

  // Effect: Load configuration when it changes
  useEffect(() => {
    const cleanup = loadConfig(config)
    return cleanup
  }, [config, loadConfig])

  // Effect: Auto-save configuration changes
  useEffect(() => {
    if (!autoSave || !hasUnsavedChanges || isInitializing) return

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    // Set new timeout for auto-save
    saveTimeoutRef.current = setTimeout(() => {
      saveConfig()
    }, saveDelay)

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [hasUnsavedChanges, autoSave, isInitializing, saveConfig, saveDelay])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [])

  return {
    // Configuration state
    config: configState,
    isInitializing,
    hasUnsavedChanges,
    skipNextConfigUpdate,

    // Configuration actions
    updateConfig,
    saveConfig,
    resetConfig,
    clearConfig,
    skipNextUpdate,

    // Validation
    validateConfig,

    // Utility functions
    getConfigValue,
    setConfigValue,

    // Refs for advanced usage
    isGeneratingImageRef,
    setSkipNextConfigUpdate,
  }
}

export default useElementConfig
